import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  FileText, Plus, Download, Clock, CheckCircle, 
  LogOut, User, ChevronRight, Trash2, AlertCircle,
  Mail, Phone
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_estate-planner-16/artifacts/7xqxcajt_LOGO.jpg';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Dashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [wills, setWills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWillId, setSelectedWillId] = useState(null);

  useEffect(() => {
    fetchWills();
  }, []);

  const fetchWills = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wills`, getAuthHeaders());
      setWills(response.data);
    } catch (error) {
      console.error('Error fetching wills:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewWill = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/wills`, {}, getAuthHeaders());
      navigate(`/questionnaire/${response.data.id}`);
    } catch (error) {
      console.error('Error creating will:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getProgressPercentage = (will) => {
    return Math.round((will.current_step / will.total_steps) * 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="dashboard-page">
      {/* Navigation */}
      <nav className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/dashboard">
                <img src={LOGO_URL} alt="Clevermoney" className="h-10 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-[#475569]">
                <User className="w-4 h-4" />
                <span className="text-sm">{user?.full_name}</span>
              </div>
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-[#475569] hover:text-[#EF4444]"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-[#0A192F] mb-2">
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-[#475569]">
            Manage your wills and track your progress below.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="dashboard-widget bg-[#0A192F] text-white" data-testid="create-will-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#C5A059] rounded-full flex items-center justify-center">
                  <Plus className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="font-heading text-xl mb-2">Start New Will</h3>
              <p className="text-gray-300 text-sm mb-4">
                Begin creating a new will with our guided process.
              </p>
              <Button 
                onClick={createNewWill}
                disabled={creating}
                className="w-full bg-[#C5A059] hover:bg-[#997B40] text-white"
                data-testid="create-will-btn"
              >
                {creating ? 'Creating...' : 'Create Will'}
              </Button>
            </CardContent>
          </Card>

          <Card className="dashboard-widget" data-testid="progress-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#FDFBF7] rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#C5A059]" />
                </div>
              </div>
              <h3 className="font-heading text-xl text-[#0A192F] mb-2">In Progress</h3>
              <p className="text-[#475569] text-sm mb-4">
                {wills.filter(w => !w.is_complete).length} will(s) in progress
              </p>
              <div className="text-3xl font-bold text-[#0A192F]">
                {wills.filter(w => !w.is_complete).length}
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget" data-testid="completed-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#FDFBF7] rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
              <h3 className="font-heading text-xl text-[#0A192F] mb-2">Completed</h3>
              <p className="text-[#475569] text-sm mb-4">
                {wills.filter(w => w.is_complete).length} will(s) completed
              </p>
              <div className="text-3xl font-bold text-[#10B981]">
                {wills.filter(w => w.is_complete).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Wills List */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl text-[#0A192F] mb-4">Your Wills</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : wills.length === 0 ? (
            <Card className="text-center py-12" data-testid="no-wills-card">
              <CardContent>
                <FileText className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="font-heading text-xl text-[#0A192F] mb-2">No Wills Yet</h3>
                <p className="text-[#475569] mb-6">
                  Start creating your first will to secure your legacy.
                </p>
                <Button 
                  onClick={createNewWill}
                  disabled={creating}
                  className="bg-[#C5A059] hover:bg-[#997B40]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Will
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {wills.map((will) => (
                <Card key={will.id} className="hover:shadow-md transition-shadow" data-testid={`will-card-${will.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="w-5 h-5 text-[#C5A059]" />
                          <h3 className="font-heading text-lg text-[#0A192F]">
                            {will.will_type_name}
                          </h3>
                          {will.is_complete && (
                            <span className="bg-[#10B981] text-white text-xs px-2 py-1 rounded-full">
                              Complete
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#475569] mb-3">
                          Created: {formatDate(will.created_at)} • 
                          Updated: {formatDate(will.updated_at)}
                        </p>
                        {!will.is_complete && (
                          <div className="max-w-xs">
                            <div className="flex justify-between text-sm text-[#475569] mb-1">
                              <span>Progress</span>
                              <span>{getProgressPercentage(will)}%</span>
                            </div>
                            <Progress value={getProgressPercentage(will)} className="h-2" />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {will.is_complete ? (
                          <>
                            <a 
                              href={`${API_URL}/api/wills/${will.id}/download/will`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="outline" className="border-[#C5A059] text-[#C5A059]" data-testid={`download-will-${will.id}`}>
                                <Download className="w-4 h-4 mr-2" />
                                Download Will
                              </Button>
                            </a>
                            <a 
                              href={`${API_URL}/api/wills/${will.id}/download/questionnaire`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="outline" data-testid={`download-questionnaire-${will.id}`}>
                                <Download className="w-4 h-4 mr-2" />
                                Questionnaire
                              </Button>
                            </a>
                          </>
                        ) : (
                          <Link to={`/questionnaire/${will.id}`}>
                            <Button className="bg-[#0A192F] hover:bg-[#172A46]" data-testid={`continue-will-${will.id}`}>
                              Continue
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Support Section */}
        <Card className="bg-[#172A46] text-white" data-testid="support-section">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-xl mb-2">Need Help?</h3>
                <p className="text-gray-300 text-sm">
                  Our team is here to assist you with any questions about your will.
                </p>
              </div>
              <div className="flex gap-4">
                <a href="mailto:info@clevermoney.ie" className="flex items-center gap-2 text-[#C5A059] hover:underline">
                  <Mail className="w-4 h-4" />
                  info@clevermoney.ie
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
